import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import ScheduleGrid from './ScheduleGrid';
import { classList } from '../data/classList';
import { TimeTableType } from '../types/schedule';
import './ScheduleContainer.css';

// 预加载 src/data 下的所有 .js 课表模块，构建 { 文件名(无扩展): 模块默认导出 }
// 排除非班级文件：classList.js、sampleSchedule.js
const dataModules = (() => {
  try {
    const ctx = require.context('../data', false, /\.js$/);
    const map = {};
    ctx.keys().forEach((key) => {
      const base = key.replace(/^\.\//, '').replace(/\.js$/, '');
      if (base === 'classList' || base === 'sampleSchedule') return;
      const mod = ctx(key);
      map[base] = mod.default || mod;
    });
    return map;
  } catch (e) {
    console.warn('数据模块预加载失败：', e);
    return {};
  }
})();

const ScheduleContainer = () => {
  // 本地存储 key（版本化，避免旧结构冲突）
  const LS_KEYS = useRef({
    classes: 'kb_selected_classes_v1', // string[]
    current: 'kb_current_tab_v1',      // number (fallback)
    currentName: 'kb_current_name_v1'  // string (preferred)
  });

  // 同步初始化：从本地缓存恢复 Tab 列表与当前索引（先用占位，稍后异步注入数据）
  const [currentTab, setCurrentTab] = useState(() => {
    try {
      const raw = localStorage.getItem('kb_selected_classes_v1');
      const names = raw ? JSON.parse(raw) : [];
      if (Array.isArray(names) && names.length > 0) {
        const curName = localStorage.getItem('kb_current_name_v1') || '';
        if (curName) {
          const idx = names.findIndex(n => n === curName);
          if (idx >= 0) return idx;
        }
        const idxRaw = localStorage.getItem('kb_current_tab_v1');
        const idx = parseInt(idxRaw || '0', 10);
        return Number.isNaN(idx) ? 0 : Math.max(0, Math.min(idx, names.length - 1));
      }
    } catch {}
    return 0;
  });
  const [slideDir, setSlideDir] = useState('left'); // 进入动效方向：left 表示从右向左进入
  const [dragX, setDragX] = useState(0); // 跟随手指的位移
  const [dragging, setDragging] = useState(false);
  const [tabList, setTabList] = useState(() => {
    try {
      const raw = localStorage.getItem('kb_selected_classes_v1');
      const names = raw ? JSON.parse(raw) : [];
      if (Array.isArray(names) && names.length > 0) {
        // 先用占位对象，稍后异步加载具体数据
        const classes = names.map(n => ({ name: n, type: 'class', Semester: null }));
        return [...classes, { name: '班级选择', type: 'manager' }];
      }
    } catch {}
    return [{ name: '班级选择', type: 'manager' }];
  });
  const [addData, setAddData] = useState({
    search: '',
    filterList: [],
    show: false,
    closing: false,
  });

  // 运行时配置：从 public/config.json 读取
  const [runtimeCfg, setRuntimeCfg] = useState({});
  const [extClassList, setExtClassList] = useState([]); // 外部班级列表（若提供）

  // 背景配置（图片与遮罩透明度）
  const [bgConfig, setBgConfig] = useState(() => {
    try {
      const raw = localStorage.getItem('kb_bg_cfg_v1');
      if (raw) return JSON.parse(raw);
    } catch {}
    return {
      type: 'default', // 'default' | 'upload' | 'none' | 'preset'
      src: require('../assets/23f27b2e1f875ada680a25480006e011.jpg'),
      presetId: 'default1', // 预设壁纸ID
      maskOpacity: 0.18, // 遮罩不透明度（0~0.6 推荐）
      courseOpacity: 0.90, // 课程块透明度（0.4~1 推荐）
      panelOpacity: 0.96, // 面板（白色背景块）透明度（0.6~1 推荐）
      courseTextColor: '#0f1b2d', // 课程块文字颜色
    };
  });
  const [wallpapers, setWallpapers] = useState([]);
  const [bgDlgOpen, setBgDlgOpen] = useState(false);
  const uploadInputRef = useRef(null);
  const titleRef = useRef(null);
  const [bgHintShow, setBgHintShow] = useState(false);
  const [bgHintPos, setBgHintPos] = useState({ top: 0, left: 0 });
  const [weekHintShow, setWeekHintShow] = useState(false);
  const [weekHintPos, setWeekHintPos] = useState({ top: 0, left: 0 });

  // 顶部广告资源（存在则覆盖标题）
  const [topAdUrl, setTopAdUrl] = useState('');
  const [topAdLink, setTopAdLink] = useState('');
  const [showTopAd, setShowTopAd] = useState(false);

  // LS_KEYS 已提前定义

  useEffect(() => {
    const loadCfg = async () => {
      try {
        const res = await fetch('/config.json', { cache: 'no-store' });
        if (!res.ok) return; // 未提供配置则忽略
        const cfg = await res.json();
        setRuntimeCfg(cfg || {});
        
        // 自动加载 wallpaper 目录下的图片
        await loadWallpapersFromDirectory();
        
        // 若提供外部班级列表 URL，尝试加载
        if (cfg && cfg.classListUrl) {
          try {
            const lr = await fetch(cfg.classListUrl, { cache: 'no-store' });
            if (lr.ok) {
              const arr = await lr.json();
              if (Array.isArray(arr)) setExtClassList(arr);
            }
          } catch {}
        }
      } catch {}
    };
    loadCfg();
  }, []);

  // 加载广告配置：优先读取 ads/config.json，支持指定广告文件名和链接
  useEffect(() => {
    const probeAds = async () => {
      try {
        let foundUrl = '';
        let foundLink = '';

        // 优先读取 ads/config.json：{ enabled: true, name: "pangxie.gif", link: "https://..." }
        try {
          const cr = await fetch('/ads/config.json', { cache: 'no-store' });
          if (cr.ok) {
            const cfg = await cr.json();
            if (cfg && cfg.enabled !== false && cfg.name) {
              foundUrl = `/ads/${encodeURIComponent(cfg.name)}`;
              if (typeof cfg.link === 'string') foundLink = cfg.link;
            }
          }
        } catch {}

        // 回退：尝试 manifest.json
        if (!foundUrl) {
          try {
            const mr = await fetch('/ads/manifest.json', { cache: 'no-store' });
            if (mr.ok) {
              const manifest = await mr.json();
              if (manifest && Array.isArray(manifest.files) && manifest.files.length > 0) {
                const name = manifest.files[0];
                if (name && typeof name === 'string') foundUrl = `/ads/${encodeURIComponent(name)}`;
              }
              if (manifest && typeof manifest.link === 'string') foundLink = manifest.link;
            }
          } catch {}
        }

        if (foundUrl) {
          setTopAdUrl(foundUrl);
          setTopAdLink(foundLink || 'https://mp.weixin.qq.com/s/fem4rPw-fagsx6UzbNWOyA');
          setShowTopAd(true);
        } else {
          setTopAdUrl('');
          setTopAdLink('');
          setShowTopAd(false);
        }
      } catch {
        setTopAdUrl('');
        setTopAdLink('');
        setShowTopAd(false);
      }
    };
    probeAds();
  }, []);

  // 从 wallpaper 目录加载所有图片文件
  const loadWallpapersFromDirectory = async () => {
    try {
      // 尝试从配置文件获取壁纸列表，如果没有则使用默认扫描
      let imageFiles = [];
      
      try {
        const manifestRes = await fetch('/wallpaper/manifest.json', { cache: 'no-store' });
        if (manifestRes.ok) {
          const manifest = await manifestRes.json();
          if (manifest.files && Array.isArray(manifest.files)) {
            imageFiles = manifest.files;
            console.log('从 manifest.json 加载壁纸列表:', imageFiles);
          }
        } else {
          console.log('manifest.json 不存在，使用默认列表');
        }
      } catch (e) {
        console.log('读取 manifest.json 失败:', e);
      }
      
      // 如果 manifest.json 不存在或读取失败，使用默认列表
      if (imageFiles.length === 0) {
        imageFiles = [
          'doro骑士与哈基米.jpg',
          'wmy.jpg', 
          '乌萨奇.jpg',
          '叶佳怡.jpg',
          '小田同学.jpg',
          '御坂美琴.jpg',
          '我们需要你.jpg',
          '曲运家.jpg',
          '柯兰.jpg',
          '艾一.jpg',
          '虹夏.jpg',
          '走一个.jpg'
        ];
      }
      
      const wallpaperList = [];
      
      // 检查每个文件是否存在
      for (const fileName of imageFiles) {
        try {
          const response = await fetch(`/wallpaper/${fileName}`, { method: 'HEAD', cache: 'no-store' });
          if (response.ok) {
            const name = fileName.replace(/\.[^/.]+$/, ''); // 去掉扩展名
            wallpaperList.push({
              id: fileName.replace(/\.[^/.]+$/, '').replace(/[^a-zA-Z0-9\u4e00-\u9fa5]/g, '_'), // 生成安全的ID，支持中文
              name: name,
              url: `/wallpaper/${fileName}`
            });
            console.log('找到壁纸文件:', fileName);
          } else {
            console.log('壁纸文件不存在:', fileName);
          }
        } catch (e) {
          console.log('检查壁纸文件失败:', fileName, e);
        }
      }
      
      console.log('最终壁纸列表:', wallpaperList);
      
      setWallpapers(wallpaperList);
    } catch (e) {
      console.warn('加载壁纸目录失败:', e);
    }
  };

  // 首屏：引导提示（壁纸 + 切换周）仅出现一次，两个提示同时显示，3秒后自动关闭
  useEffect(() => {
    try {
      const shownBg = localStorage.getItem('kb_bg_hint_v1');
      const shownWeek = localStorage.getItem('kb_week_hint_v1');
      if (shownBg && shownWeek) return;
    } catch {}
    let autoTimerId;
    const timer = setTimeout(() => {
      // 1) 壁纸提示：锚定标题
      if (titleRef.current) {
        const r = titleRef.current.getBoundingClientRect();
        setBgHintPos({ top: Math.round(r.bottom + 6), left: Math.round(r.left + r.width / 2) });
        setBgHintShow(true);
      }
      // 2) 切换周提示：放在屏幕底部中间
      try {
        const iw = window.innerWidth || document.documentElement.clientWidth;
        const ih = window.innerHeight || document.documentElement.clientHeight;
        setWeekHintPos({ top: Math.max(100, Math.round(ih - 760)), left: Math.round(iw / 2) });
        setWeekHintShow(true);
      } catch {}
      // 3) 自动关闭（3秒）并持久化
      autoTimerId = setTimeout(() => {
        closeBgHint();
      }, 3000);
    }, 500);
    return () => {
      clearTimeout(timer);
      if (autoTimerId) clearTimeout(autoTimerId);
    };
  }, []);

  const closeBgHint = () => {
    setBgHintShow(false);
    setWeekHintShow(false);
    try {
      localStorage.setItem('kb_bg_hint_v1', '1');
      localStorage.setItem('kb_week_hint_v1', '1');
    } catch {}
  };

  // 持久化背景配置
  useEffect(() => {
    try { localStorage.setItem('kb_bg_cfg_v1', JSON.stringify(bgConfig)); } catch {}
  }, [bgConfig]);

  // 配置到达后：为尚未加载成功的班级补齐数据（解决刷新后需切换一次的问题）
  useEffect(() => {
    if (!runtimeCfg) return;
    const names = tabList.filter(it => it.type === 'class' && !it.Semester).map(it => it.name);
    if (names.length === 0) return;
    (async () => {
      for (const n of names) {
        await ensureSemesterLoaded(n);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runtimeCfg]);

  // 抽取：按名称加载一个 Semester（外部 JSON 优先，回退到内置 js）
  const loadSemesterByName = async (cls, cfg) => {
    let Semester = null;
    const base = (cfg && cfg.externalBase) || (runtimeCfg && runtimeCfg.externalBase) || '';
    if (base) {
      const url = `${base}/${encodeURIComponent(cls)}.json`;
      try {
        const res = await fetch(url, { cache: 'no-store' });
        if (res.ok) {
          Semester = await res.json();
        }
      } catch {}
    }
    if (!Semester) {
      // 先查预加载
      Semester = dataModules[cls];
    }
    if (!Semester) {
      try {
        const mod = await import(`../data/${cls}.js`);
        Semester = mod.default || mod;
      } catch {}
    }
    return Semester;
  };

  // 确保指定名称的班级数据已加载（若未加载则异步加载并写回）
  const ensureSemesterLoaded = async (name) => {
    try {
      const idx = tabList.findIndex(it => it.type === 'class' && it.name === name);
      if (idx < 0) return;
      if (tabList[idx].Semester) return;
      const sem = await loadSemesterByName(name);
      if (!sem) return;
      setTabList(prev => {
        const next = [...prev];
        const i = next.findIndex(it => it.type === 'class' && it.name === name);
        if (i >= 0) next[i] = { ...next[i], Semester: sem };
        return next;
      });
    } catch {}
  };

  // 启动时：从本地恢复选中的班级与当前 Tab
  useEffect(() => {
    const restore = async () => {
      try {
        const raw = localStorage.getItem(LS_KEYS.current.classes);
        const names = raw ? JSON.parse(raw) : [];
        if (!Array.isArray(names) || names.length === 0) return;
        // 异步补齐各班级数据，保留现有顺序
        const loaded = await Promise.all(names.map(async (name) => ({
          name,
          type: 'class',
          Semester: await loadSemesterByName(name)
        })));
        // 不丢弃加载失败的项，保留占位，后续按需加载
        const list = loaded;
        if (list.length > 0) {
          setTabList([...list, { name: '班级选择', type: 'manager' }]);
          // 优先根据名称恢复当前 Tab
          const curName = localStorage.getItem(LS_KEYS.current.currentName) || '';
          if (curName) {
            const idxByName = list.findIndex(it => it.name === curName);
            if (idxByName >= 0) {
              setCurrentTab(idxByName);
              return;
            }
          }
          // 回退：根据旧的索引（不含 manager 的范围）
          const idxRaw = localStorage.getItem(LS_KEYS.current.current);
          let idx = parseInt(idxRaw || '0', 10);
          if (Number.isNaN(idx)) idx = 0;
          if (idx < 0 || idx >= list.length) idx = 0;
          setCurrentTab(idx);
        }
      } catch {}
    };
    restore();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 每次 tabList 或 currentTab 变更时，写入本地存储
  useEffect(() => {
    try {
      const names = tabList.filter(it => it.type === 'class').map(it => it.name);
      localStorage.setItem(LS_KEYS.current.classes, JSON.stringify(names));
      // 存当前页签：优先存班级名
      const curr = tabList[currentTab];
      if (curr && curr.type === 'class') {
        localStorage.setItem(LS_KEYS.current.currentName, curr.name);
      } else {
        localStorage.removeItem(LS_KEYS.current.currentName);
      }
      // 兼容旧索引：当“班级选择”在末尾时，仅对班级范围有效
      localStorage.setItem(LS_KEYS.current.current, String(currentTab));
    } catch {}
  }, [tabList, currentTab]);

  // Tab切换
  const tabNav = (index) => {
    setSlideDir(index > currentTab ? 'left' : 'right');
    setCurrentTab(index);
  };

  // ===== 背景图片：标题点击打开设置对话框 =====
  const onTitleClick = () => setBgDlgOpen(true);
  const closeBgDlg = () => setBgDlgOpen(false);
  const onBgUploadClick = () => {
    if (uploadInputRef.current) uploadInputRef.current.click();
  };
  const onBgFileChange = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      setBgConfig(prev => ({
        ...prev,
        type: 'upload',
        src: reader.result,
        maskOpacity: prev.maskOpacity ?? 0.18,
      }));
    };
    reader.readAsDataURL(file);
    // 重置 input，方便重复选择同一文件
    e.target.value = '';
  };
  const onBgPresetChange = (e) => {
    const val = e.target.value;
    if (val === 'none') {
      setBgConfig(prev => ({ ...prev, type: 'none', src: '', presetId: '', maskOpacity: prev.maskOpacity ?? 0.18 }));
    } else if (val === 'default1') {
      setBgConfig(prev => ({ ...prev, type: 'default', src: require('../assets/23f27b2e1f875ada680a25480006e011.jpg'), presetId: 'default1', maskOpacity: prev.maskOpacity ?? 0.18 }));
    } else {
      // 从壁纸列表中查找
      const wallpaper = wallpapers.find(w => w.id === val);
      if (wallpaper) {
        setBgConfig(prev => ({ ...prev, type: 'preset', src: wallpaper.url, presetId: val, maskOpacity: prev.maskOpacity ?? 0.18 }));
      }
    }
  };
  const onMaskOpacityChange = (e) => {
    const v = Number(e.target.value);
    const clamped = Math.max(0, Math.min(0.6, v));
    setBgConfig(prev => ({ ...prev, maskOpacity: clamped }));
  };
  const onCourseOpacityChange = (e) => {
    const v = Number(e.target.value);
    const clamped = Math.max(0.4, Math.min(1, v));
    setBgConfig(prev => ({ ...prev, courseOpacity: clamped }));
  };
  const onPanelOpacityChange = (e) => {
    const v = Number(e.target.value);
    const clamped = Math.max(0.6, Math.min(1, v));
    setBgConfig(prev => ({ ...prev, panelOpacity: clamped }));
  };
  const onCourseTextColorChange = (e) => {
    const val = e.target.value || '#0f1b2d';
    setBgConfig(prev => ({ ...prev, courseTextColor: val }));
  };

  // 当切到某个班级标签且数据未加载时，懒加载补齐
  useEffect(() => {
    const curr = tabList[currentTab];
    if (curr && curr.type === 'class' && !curr.Semester) {
      ensureSemesterLoaded(curr.name);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTab, tabList]);

  // =========================
  // 横向滑动切换课表（当只有一个课表时允许切到“班级选择”）
  // =========================
  const touchStartX = useRef(0);
  const touchStartY = useRef(0);
  const swiping = useRef(false);

  // 查找下一个可切换的 Tab：优先下一个“课表”，否则允许到任意（如 manager）
  const findNextTab = (fromIdx) => {
    // 先找下一个课表
    for (let i = fromIdx + 1; i < tabList.length; i++) {
      if (tabList[i]?.type === 'class') return i;
    }
    // 若没有课表，找任意下一个（例如 manager）
    if (fromIdx + 1 < tabList.length) return fromIdx + 1;
    return fromIdx;
  };

  // 查找上一个可切换的 Tab：优先上一个“课表”，否则允许到任意（如 manager）
  const findPrevTab = (fromIdx) => {
    for (let i = fromIdx - 1; i >= 0; i--) {
      if (tabList[i]?.type === 'class') return i;
    }
    if (fromIdx - 1 >= 0) return fromIdx - 1;
    return fromIdx;
  };

  // 拖拽容器宽度，用于 40% 阈值判断
  const contentInnerRef = useRef(null);
  const gestureLockRef = useRef('none'); // 'none' | 'x' | 'y'
  const lastAppliedXRef = useRef(0); // 降低 setState 频率
  const rafIdRef = useRef(0);
  const nextXRef = useRef(0);
  const swipeJustHappenedRef = useRef(false);

  const handleTouchStart = (e) => {
    if (!e.touches || e.touches.length === 0) return;
    const t = e.touches[0];
    touchStartX.current = t.clientX;
    touchStartY.current = t.clientY;
    swiping.current = true;
    setDragging(false); // 仅当确定为横向滑动后再置为 true
    setDragX(0);
    gestureLockRef.current = 'none';
    lastAppliedXRef.current = 0;
  };

  const handleTouchMove = (e) => {
    if (!swiping.current || !e.touches || e.touches.length === 0) return;
    const t = e.touches[0];
    const dx = t.clientX - touchStartX.current;
    const dy = t.clientY - touchStartY.current;
    const activate = 9; // 启动横向手势阈值(<10px)，避免点一下就滑
    if (gestureLockRef.current === 'none') {
      if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > activate) {
        gestureLockRef.current = 'x';
        setDragging(true);
      } else if (Math.abs(dy) > Math.abs(dx) && Math.abs(dy) > activate) {
        gestureLockRef.current = 'y';
      }
    }

    if (gestureLockRef.current !== 'x') return; // 未锁定为横向则不处理

    // 横向联动，边缘做阻尼
    const hasPrev = findPrevTab(currentTab) !== currentTab;
    const hasNext = findNextTab(currentTab) !== currentTab;
    let nextX = dx;
    if (dx > 0 && !hasPrev) {
      nextX = dx * 0.3;
    } else if (dx < 0 && !hasNext) {
      nextX = dx * 0.3;
    }
    // rAF 节流：每帧最多 setState 一次
    if (Math.abs(nextX - lastAppliedXRef.current) > 1) {
      nextXRef.current = nextX;
      if (!rafIdRef.current) {
        rafIdRef.current = requestAnimationFrame(() => {
          rafIdRef.current = 0;
          const val = nextXRef.current;
          lastAppliedXRef.current = val;
          setDragX(val);
        });
      }
    }
  };

  const handleTouchEnd = (e) => {
    if (!swiping.current) return;
    swiping.current = false;
    const touch = (e.changedTouches && e.changedTouches[0]) || null;
    if (!touch) return;
    const dx = touch.clientX - touchStartX.current;
    const dy = touch.clientY - touchStartY.current;
    const absX = Math.abs(dx);
    const absY = Math.abs(dy);
    const verticalLimit = 60;
    const width = (contentInnerRef.current && contentInnerRef.current.getBoundingClientRect().width) || window.innerWidth || 0;
    const threshold = width * 0.2; // 20% 宽度作为切换阈值
    // 结束时先回弹
    setDragging(false);
    setDragX(0);
    gestureLockRef.current = 'none';
    // 若发生明显横向拖动，用于阻止接下来的点击
    if (absX > 9 && absY < 40) {
      swipeJustHappenedRef.current = true;
      setTimeout(() => { swipeJustHappenedRef.current = false; }, 180);
    }
    if (absX < threshold || absY > verticalLimit) return; // 明显横向且非纵向

    if (dx < 0) {
      // 左滑：下一个 Tab（优先课表，否则到 manager）
      const hasNext = findNextTab(currentTab) !== currentTab;
      if (!hasNext) return; // 右侧没新页面则不切换
      const nextIdx = findNextTab(currentTab);
      if (nextIdx !== currentTab) {
        setSlideDir('left');
        setCurrentTab(nextIdx);
      }
    } else if (dx > 0) {
      // 右滑：上一个 Tab（优先课表，否则到 manager）
      const hasPrev = findPrevTab(currentTab) !== currentTab;
      if (!hasPrev) return; // 左侧没新页面则不切换
      const prevIdx = findPrevTab(currentTab);
      if (prevIdx !== currentTab) {
        setSlideDir('right');
        setCurrentTab(prevIdx);
      }
    }
  };

  const handleClickCapture = (e) => {
    if (swipeJustHappenedRef.current) {
      e.preventDefault();
      e.stopPropagation();
      swipeJustHappenedRef.current = false;
    }
  };

  // 删除课程表（无确认，直接删除）
  const delClass = (index, name) => {
    const newTabList = [...tabList];
    newTabList.splice(index, 1);
    // 确保 manager 存在且在末尾
    const hasManager = newTabList.some(it => it.type === 'manager');
    const classesOnly = newTabList.filter(it => it.type === 'class');
    const finalList = hasManager ? [...classesOnly, { name: '班级选择', type: 'manager' }] : [...classesOnly, { name: '班级选择', type: 'manager' }];
    setTabList(finalList);
    setCurrentTab(0);
  };

  // 打开添加弹窗
  const openAdd = () => {
    cleanAdd();
    setAddData(prev => ({ ...prev, show: true, closing: false }));
  };

  // 关闭添加弹窗
  const closeAdd = () => {
    // 触发离场动画，动画结束后再卸载
    setAddData(prev => ({ ...prev, closing: true }));
    setTimeout(() => {
      setAddData(prev => ({ ...prev, show: false, closing: false, search: '', filterList: [] }));
    }, 200); // 与 CSS leave 动画时长一致
  };

  // 清空搜索
  const cleanAdd = () => {
    setAddData(prev => ({ ...prev, search: '', filterList: [] }));
  };

  // 搜索班级
  const getValue = (e) => {
    const val = e.target.value;
    setAddData(prev => ({ ...prev, search: val }));

    const trimmed = String(val || '').trim();
    // 输入 "." 或 "。" 时，展示所有班级
    if (trimmed === '.' || trimmed === '。') {
      setAddData(prev => ({ ...prev, filterList: classList.slice(0) }));
      return;
    }

    // 数据源：优先外部班级列表，其次内置 classList
    const sourceList = extClassList && extClassList.length > 0 ? extClassList : classList;
    if (trimmed.length > 0) {
      setTimeout(() => {
        const arr = sourceList.filter(item => item.includes(trimmed));
        setAddData(prev => ({ ...prev, filterList: arr }));
      }, 250);
    } else {
      setAddData(prev => ({ ...prev, filterList: [] }));
    }
  };

  // 选择班级
  const chooseClass = async (className) => {
    const cls = String(className || '').trim();
    console.log('[chooseClass] click:', cls);
    if (!cls) return;

    // 直接添加，避免有些环境下 window.confirm 被拦截导致后续不执行
    console.log('[chooseClass] proceed add without confirm');

    // 防重（使用当前状态检查）
    if (tabList.some(item => item.name === cls)) {
      alert('已存在该班级');
      return;
    }

    try {
      const Semester = await loadSemesterByName(cls);
      if (!Semester) throw new Error('not_found');

      const newTab = { name: cls, type: 'class', Semester };
      // 使用函数式更新；去重并保证 manager 在末尾
      setTabList(prev => {
        const classesOnly = prev.filter(it => it.type === 'class' && it.name !== cls);
        return [newTab, ...classesOnly, { name: '班级选择', type: 'manager' }];
      });
      // 直接切换到新添加的班级标签（索引 0）
      setCurrentTab(0);
      closeAdd();
      // 不再弹窗提示
    } catch (e) {
      console.error('加载班级数据失败: ', e);
      alert('未找到该班级的数据文件，请确认文件是否存在于 src/data/ 下');
    }
  };

  return (
    <div className="schedule-container">
      {/* 背景层 */}
      <div className="bg-layer" aria-hidden="true">
        {bgConfig.type !== 'none' && bgConfig.src ? (
          <div
            className="bg-image"
            style={{ backgroundImage: `url(${bgConfig.src})` }}
          />
        ) : null}
        <div className="bg-mask" style={{ opacity: bgConfig.maskOpacity }} />
      </div>
      {/* 自定义头部 */}
      <div className="header">
        <h1 ref={titleRef} onClick={onTitleClick} title="点击更换背景">沈工课程表</h1>
        {/* 隐藏的文件选择器 */}
        <input
          type="file"
          accept="image/*"
          ref={uploadInputRef}
          onChange={onBgFileChange}
          style={{ display: 'none' }}
        />
        {/* 顶部广告：覆盖标题区域（若 ads 目录无素材，则不显示） */}
        {showTopAd && (
          <a
            className="top-ad"
            href={topAdLink}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="推广广告"
            title="推广"
            style={{ backgroundImage: `url(${topAdUrl})` }}
          />
        )}
      </div>
      {addData.show && createPortal(
        (
          <div className={`modal add-class-modal ${addData.closing ? 'leave' : 'enter'}`}>
            <div className="dialog">
              <div className="head">
                <div className="head_input">
                  <input
                    type="text"
                    placeholder="搜索"
                    value={addData.search}
                    onChange={getValue}
                  />
                </div>
                <div className="sha_icon" onClick={closeAdd}>
                  取消
                </div>
              </div>
              <div className="con">
                {addData.filterList.length > 0 ? (
                  addData.filterList.map((item, index) => (
                    <div key={index} className="item" onClick={() => chooseClass(item)}>
                      {item}
                    </div>
                  ))
                ) : (
                  <div className="item empty">输入一点，奇迹将现</div>
                )}
              </div>
            </div>
          </div>
        ),
        document.body
      )}

      {/* 首屏提示（合并）：壁纸 + 切换周；同一个遮罩中同时展示 */}
      {bgHintShow && createPortal(
        (
          <div className="bg-hint-mask" onClick={closeBgHint}>
            <div className="bg-hint-arrow" style={{ position: 'fixed', top: `${bgHintPos.top}px`, left: `${bgHintPos.left}px`, transform: 'translateX(-50%) rotate(180deg)' }} />
            <div className="bg-hint-bubble" style={{ position: 'fixed', top: `${bgHintPos.top + 10}px`, left: `${bgHintPos.left}px`, transform: 'translateX(-50%)' }}>
              点击此处可以更换壁纸
            </div>
            {weekHintShow && (
              <div className="bg-hint-row" style={{ position: 'fixed', top: `${weekHintPos.top}px`, left: `${weekHintPos.left}px`, transform: 'translateX(-50%)' }}>
                <div className="bg-hint-arrow" style={{ transform: 'rotate(90deg)' }} />
                <div className="bg-hint-bubble">点此可以切换周</div>
              </div>
            )}
          </div>
        ),
        document.body
      )}

      {/* 背景设置对话框（独立渲染，避免被上面条件包裹） */}
      {bgDlgOpen && createPortal(
        (
          <div className="schedule-modal show" onClick={closeBgDlg}>
            <div className="simple-dialog enter-anim bg-settings-card" onClick={(e) => e.stopPropagation()}>
              <div className="settings-title">外观设置</div>
              <div className="settings-section">
                <div className="settings-row">
                  <div className="settings-label">背景图片</div>
                  <div className="settings-field">
                    <div className="btn" onClick={onBgUploadClick}>上传本地图片</div>
                  </div>
                </div>
                <div className="settings-row">
                  <div className="settings-label">预设图片</div>
                  <div className="settings-field">
                    <select onChange={onBgPresetChange} value={bgConfig.type === 'none' ? 'none' : (bgConfig.presetId || 'default1')}>
                      <option value="default1">默认图片（校园竖图）</option>
                      {wallpapers.map(wallpaper => (
                        <option key={wallpaper.id} value={wallpaper.id}>{wallpaper.name}</option>
                      ))}
                      <option value="none">无图片</option>
                    </select>
                  </div>
                </div>
              </div>

              <div className="settings-section">
                <div className="settings-row">
                  <div className="settings-label">遮罩不透明度</div>
                  <div className="settings-field">
                    <input type="range" min="0" max="0.6" step="0.02" value={bgConfig.maskOpacity ?? 0.18} onChange={onMaskOpacityChange} />
                    <span className="value">{(bgConfig.maskOpacity ?? 0.18).toFixed(2)}</span>
                  </div>
                </div>
                <div className="settings-row">
                  <div className="settings-label">背景块透明度</div>
                  <div className="settings-field">
                    <input type="range" min="0.6" max="1" step="0.02" value={bgConfig.panelOpacity ?? 0.96} onChange={onPanelOpacityChange} />
                    <span className="value">{(bgConfig.panelOpacity ?? 0.96).toFixed(2)}</span>
                  </div>
                </div>
                <div className="settings-row">
                  <div className="settings-label">课程块透明度</div>
                  <div className="settings-field">
                    <input type="range" min="0.4" max="1" step="0.02" value={bgConfig.courseOpacity ?? 0.9} onChange={onCourseOpacityChange} />
                    <span className="value">{(bgConfig.courseOpacity ?? 0.9).toFixed(2)}</span>
                  </div>
                </div>
                <div className="settings-row">
                  <div className="settings-label">课程文字颜色</div>
                  <div className="settings-field color-field">
                    <input type="color" value={bgConfig.courseTextColor || '#0f1b2d'} onChange={onCourseTextColorChange} />
                    <input type="text" value={bgConfig.courseTextColor || '#0f1b2d'} onChange={onCourseTextColorChange} />
                  </div>
                </div>
              </div>

              <div className="settings-actions">
                <div className="btn ghost" onClick={closeBgDlg} style={{ marginRight: 8 }}>取消</div>
                <div className="btn" onClick={closeBgDlg}>完成</div>
              </div>
            </div>
          </div>
        ),
        document.body
      )}

      {/* Tab导航 */}
      <div className="scroll-wrapper">
        <div className="scroll-view">
          {tabList.map((item, index) => (
            <div 
              key={index}
              className="navigate-item"
              onClick={() => tabNav(index)}
            >
              <div className={`names ${currentTab === index ? 'active' : ''}`}>
                {item.name}
              </div>
              {currentTab === index && (
                <div className="currtline active"></div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* 内容区域 */}
      <div
        className="content-area"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {(() => {
          // 渲染单页内容的函数（恢复为单面板）
          const renderTab = (idx) => {
            if (!tabList[idx]) return null;
            if (tabList[idx].type !== 'manager') {
              const curr = tabList[idx];
              if (!curr.Semester) {
                // 加载占位 + 触发懒加载
                ensureSemesterLoaded(curr.name);
                return (
                  <div className={`tab-panel ${!dragging ? `slide-in-${slideDir}` : ''}`} key={`tab-${idx}`}>
                    <div style={{ padding: 24, textAlign: 'center', color: '#888' }}>正在加载课表数据...</div>
                  </div>
                );
              }
              return (
                <div className={`tab-panel ${!dragging ? `slide-in-${slideDir}` : ''}`} key={`tab-${idx}`}>
                  <ScheduleGrid
                    startTime={(runtimeCfg && runtimeCfg.startTime) || '2025-8-25'}
                    semester={curr.Semester}
                    timeTable={TimeTableType}
                    enableWeekSwipe={true}
                    courseOpacity={(bgConfig && bgConfig.courseOpacity) || 0.9}
                    panelOpacity={(bgConfig && bgConfig.panelOpacity) || 0.96}
                    courseTextColor={(bgConfig && bgConfig.courseTextColor) || '#0f1b2d'}
                  />
                </div>
              );
            }
            return (
              <div className={`choose tab-panel ${!dragging ? `slide-in-${slideDir}` : ''}`} key={`tab-${idx}`}>
                <div className="list">
                  <div className="list-item add-btn" onClick={openAdd}>
                    添加班级课表
                  </div>
                </div>
                {tabList.map((item, i) => (
                  item.type !== 'manager' && (
                    <div key={i} className="list">
                      <div className="list-item">
                        <div className="name">{item.name}</div>
                        <div className="buttons" onClick={() => delClass(i, item.name)}>
                          删除
                        </div>
                      </div>
                    </div>
                  )
                ))}
                {/* 底部信息 */}
                <div className="choose-footer">
                  <div className="choose-footer-line">Version : 2.8</div>
                  <div className="choose-footer-line">Development by 宇凌轩 落灰</div>
                  <div className="choose-footer-line2">Operated by 大学心迹校园生活服务团队</div>
                </div>
              </div>
            );
          };
          return renderTab(currentTab);
        })()}
      </div>
    </div>
  );
};

export default ScheduleContainer;
